import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { createOperationStudent } from '../../services/operationStudentService';
import { FileSpreadsheet, Download, Search, Plus, Trash2, Save, X, Edit2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import * as XLSX from 'xlsx';

interface LicenseApplication {
    id: string;
    university: string;
    program: string;
    username?: string;
    password?: string;
    status?: string;
    fee?: string;
    result?: string;
    exams?: string;
    deadline?: string;
    notes?: string;
    cisia_username?: string;
    cisia_password?: string;
}

interface Student {
    id: string;
    first_name: string;
    last_name: string;
    is_eu: boolean;
    universitaly_data: {
        email?: string;
        password?: string;
    };
    license_applications: LicenseApplication[];
    created_at: string;
}

const STATUS_OPTIONS = [
    'TAMAMLANDI',
    'BEKLEMEDE',
    'EKSİK EVRAK',
    'İPTAL'
];

const LicenseStudentList: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState<Student[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingStudentIds, setDeletingStudentIds] = useState<Set<string>>(new Set());
    const { user } = useAuth();

    // New Student State
    // New Student State
    const [newStudent, setNewStudent] = useState({
        first_name: '',
        last_name: '',
        universitaly_email: '',
        universitaly_password: '',
        university: '',
        program: '',
        username: '',
        password: '',
        status: 'BEKLEMEDE',
        fee: '',
        result: '',
        exams: '',
        deadline: '',
        notes: '',
        cisia_username: '',
        cisia_password: ''
    });
    const [isAdding, setIsAdding] = useState(false);
    const [availableUniversities, setAvailableUniversities] = useState<{ id: string; name: string }[]>([]);

    // Column Resizing Logic
    const { columnWidths, setColumnWidths } = useTablePreferences('license-student-list', {
        first_name: 120,
        last_name: 120,
        universitaly_email: 150,
        universitaly_password: 120,
        university: 200,
        program: 180,
        username: 120,
        password: 120,
        status: 120,
        fee: 120,
        result: 120,
        exams: 150,
        deadline: 100,
        notes: 150,
        cisia_username: 120,
        cisia_password: 120,
        actions: 100
    });

    const handleMouseDown = (e: React.MouseEvent, key: string) => {
        const startX = e.clientX;
        const startWidth = columnWidths[key];

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    useEffect(() => {
        const fetchUnis = async () => {
            const { data } = await supabase.from('universities').select('id, name').order('name');
            if (data) setAvailableUniversities(data);
        };
        fetchUnis();
    }, []);

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('operation_students')
                .select('*')
                .order('created_at', { ascending: false });

            if (user?.branchId) query = query.eq('branch_id', user.branchId);
            const { data, error } = await query;

            if (error) throw error;

            const mapped = (data || []).map(s => ({
                ...s,
                universitaly_data: s.universitaly_data || {},
                license_applications: Array.isArray(s.license_applications) ? s.license_applications : []
            }));

            setStudents(mapped);
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStudentState = (studentId: string, field: string, value: any, nestedField?: string) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== studentId) return s;
            const updated = { ...s };
            if (nestedField) {
                // @ts-ignore
                updated[nestedField] = { ...updated[nestedField], [field]: value };
            } else {
                // @ts-ignore
                updated[field] = value;
            }
            return updated;
        }));
    };

    const updateAppState = (studentId: string, appId: string, field: string, value: any) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== studentId) return s;
            const updatedApps = s.license_applications.map(app => {
                if (app.id !== appId) return app;
                return { ...app, [field]: value };
            });
            return { ...s, license_applications: updatedApps };
        }));
    };

    const handleSave = async (student: Student) => {
        try {
            const { error } = await supabase
                .from('operation_students')
                .update({
                    first_name: student.first_name,
                    last_name: student.last_name,
                    universitaly_data: student.universitaly_data,
                    license_applications: student.license_applications
                })
                .eq('id', student.id);

            if (error) throw error;
            alert('Kayıt güncellendi.');
        } catch (error) {
            console.error('Error saving:', error);
            alert('Kaydetme hatası.');
        }
    };

    const handleDelete = async (studentId: string, appId: string) => {
        if (!await window.appConfirm('Bu başvuruyu silmek istediğinize emin misiniz?')) return;

        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const updatedApps = student.license_applications.filter(app => app.id !== appId);

        try {
            const { error } = await supabase
                .from('operation_students')
                .update({ license_applications: updatedApps })
                .eq('id', studentId);

            if (error) throw error;
            setStudents(prev => prev.map(s => s.id === studentId ? { ...s, license_applications: updatedApps } : s));
        } catch (error) {
            console.error('Error deleting:', error);
            alert('Silme hatası.');
        }
    };

    const handleDeleteStudent = async (student: Student) => {
        const fullName = `${student.first_name} ${student.last_name}`.trim();
        if (!await window.appConfirm(`${fullName || 'Bu öğrenci'} ve lisans başvuru bilgileri kalıcı olarak silinsin mi?`, {
            title: 'Öğrenci kaydını sil',
            confirmLabel: 'Evet, kaydı sil',
            tone: 'danger'
        })) return;

        try {
            setDeletingStudentIds(prev => new Set(prev).add(student.id));

            const { error } = await supabase
                .from('operation_students')
                .delete()
                .eq('id', student.id);

            if (error) throw error;
            setStudents(prev => prev.filter(item => item.id !== student.id));
        } catch (error: any) {
            console.error('Error deleting license student:', error);
            alert(`Öğrenci silinemedi: ${error?.message || 'Bilinmeyen hata'}`);
        } finally {
            setDeletingStudentIds(prev => {
                const next = new Set(prev);
                next.delete(student.id);
                return next;
            });
        }
    };

    const handleAddApplication = async (studentId: string) => {
        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const newApp: LicenseApplication = {
            id: crypto.randomUUID(),
            university: '',
            program: '',
            status: 'BEKLEMEDE'
        };

        const updatedApps = [...student.license_applications, newApp];

        try {
            const { error } = await supabase
                .from('operation_students')
                .update({ license_applications: updatedApps })
                .eq('id', studentId);

            if (error) throw error;

            setStudents(prev => prev.map(s => s.id === studentId ? { ...s, license_applications: updatedApps } : s));
        } catch (error) {
            console.error('Error adding application:', error);
        }
    };

    const handleAddStudent = async () => {
        if (!newStudent.first_name || !newStudent.last_name) {
            alert('Lütfen ad ve soyad giriniz.');
            return;
        }

        try {
            setIsAdding(true);

            const { data, error } = await createOperationStudent({
                p_first_name: newStudent.first_name,
                p_last_name: newStudent.last_name,
                p_branch_id: user?.branchId,
                p_program_type: 'license',
                p_university_name: '',
                p_program_name: '',
                p_created_by: user?.id
            });

            if (error) throw error;
            if (data && !data.success) throw new Error(data.error);

            const operationStudentId = data?.operation_id || data?.id;

            if (operationStudentId) {
                // Update additional details
                const updates: any = {};

                // Universitaly Data
                if (newStudent.universitaly_email || newStudent.universitaly_password) {
                    updates.universitaly_data = {
                        email: newStudent.universitaly_email,
                        password: newStudent.universitaly_password
                    };
                }

                // License Application
                const newApp: LicenseApplication = {
                    id: crypto.randomUUID(),
                    university: newStudent.university,
                    program: newStudent.program,
                    username: newStudent.username,
                    password: newStudent.password,
                    status: newStudent.status,
                    fee: newStudent.fee,
                    result: newStudent.result,
                    exams: newStudent.exams,
                    deadline: newStudent.deadline,
                    notes: newStudent.notes,
                    cisia_username: newStudent.cisia_username,
                    cisia_password: newStudent.cisia_password
                };
                updates.license_applications = [newApp];

                const { error: updateError } = await supabase
                    .from('operation_students')
                    .update(updates)
                    .eq('id', operationStudentId);

                if (updateError) console.error('Error updating new student details:', updateError);
            }

            await fetchStudents();
            setNewStudent({
                first_name: '',
                last_name: '',
                universitaly_email: '',
                universitaly_password: '',
                university: '',
                program: '',
                username: '',
                password: '',
                status: 'BEKLEMEDE',
                fee: '',
                result: '',
                exams: '',
                deadline: '',
                notes: '',
                cisia_username: '',
                cisia_password: ''
            });
            alert('Öğrenci başarıyla eklendi.');

        } catch (error: any) {
            console.error('Error adding student:', error);
            alert('Öğrenci eklenirken hata: ' + error.message);
        } finally {
            setIsAdding(false);
        }
    };

    const handleExportExcel = () => {
        const flattenData = students.flatMap(s => {
            if (s.license_applications.length === 0) return [];
            return s.license_applications.map(app => ({
                'ADI': s.first_name + (s.is_eu ? '(EU)' : ''),
                'SOYADI': s.last_name,
                'Universitaly': s.universitaly_data?.email,
                'Universitaly Şifre': s.universitaly_data?.password,
                'ÜNİVERSİTE': app.university,
                'BÖLÜM': app.program,
                'KULLANICI ADI': app.username,
                'ŞİFRE': app.password,
                'DURUMU': app.status,
                'BAŞVURU ÜCRETİ': app.fee,
                'SONUÇ': app.result,
                'Gerekli Sınavlar': app.exams,
                'DEADLINE': app.deadline,
                'NOTLAR': app.notes,
                'CISIA kullanıcı adı': app.cisia_username,
                'CISIA şifre': app.cisia_password
            }));
        });

        const ws = XLSX.utils.json_to_sheet(flattenData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lisans Başvuruları");
        XLSX.writeFile(wb, `Lisans_Ogrenci_Listesi.xlsx`);
    };

    const filteredStudents = students.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

    return (
        <div className="p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold flex items-center gap-2">
                    <FileSpreadsheet className="text-green-600" />
                    Lisans Öğrenci Başvuru Takibi
                </h1>
                <div className="flex gap-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text" placeholder="Ara..."
                            className="pl-8 pr-4 py-1.5 rounded border text-sm focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button onClick={handleExportExcel} className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700">
                        <Download size={16} /> Excel İndir
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto">
                    <table className="w-full text-[11px] text-left border-collapse min-w-[1800px]" style={{ tableLayout: 'fixed' }}>
                        <thead className="bg-[#d1d5db] dark:bg-gray-700 text-gray-800 dark:text-gray-200 uppercase font-bold text-center sticky top-0 z-10">
                            <tr>
                                {[
                                    { key: 'first_name', label: 'ADI', width: columnWidths.first_name },
                                    { key: 'last_name', label: 'SOYADI', width: columnWidths.last_name },
                                    { key: 'universitaly_email', label: 'Universitaly', width: columnWidths.universitaly_email },
                                    { key: 'universitaly_password', label: 'Universitaly Şifre', width: columnWidths.universitaly_password },
                                    { key: 'university', label: 'ÜNİVERSİTE', width: columnWidths.university },
                                    { key: 'program', label: 'BÖLÜM', width: columnWidths.program },
                                    { key: 'username', label: 'KULLANICI ADI', width: columnWidths.username },
                                    { key: 'password', label: 'ŞİFRE', width: columnWidths.password },
                                    { key: 'status', label: 'DURUMU', width: columnWidths.status },
                                    { key: 'fee', label: 'BAŞVURU ÜCRETİ', width: columnWidths.fee },
                                    { key: 'result', label: 'SONUÇ', width: columnWidths.result },
                                    { key: 'exams', label: 'Gerekli Sınavlar', width: columnWidths.exams },
                                    { key: 'deadline', label: 'DEADLINE', width: columnWidths.deadline, className: 'bg-red-600 text-white' },
                                    { key: 'notes', label: 'NOTLAR', width: columnWidths.notes, className: 'bg-yellow-400 text-black' },
                                    { key: 'cisia_username', label: 'CISIA kullanıcı adı', width: columnWidths.cisia_username },
                                    { key: 'cisia_password', label: 'CISIA şifre', width: columnWidths.cisia_password },
                                    { key: 'actions', label: 'İŞLEMLER', width: columnWidths.actions }
                                ].map((col) => (
                                    <th
                                        key={col.key}
                                        className={`relative px-2 py-2 border border-black/10 overflow-hidden text-ellipsis whitespace-nowrap group ${col.className || ''}`}
                                        style={{ width: col.width }}
                                    >
                                        {col.label}
                                        <div
                                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onMouseDown={(e) => handleMouseDown(e, col.key)}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Add New Row */}
                            <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30">
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Ad"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.first_name}
                                        onChange={e => setNewStudent({ ...newStudent, first_name: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Soyad"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.last_name}
                                        onChange={e => setNewStudent({ ...newStudent, last_name: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Universitaly Email"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.universitaly_email}
                                        onChange={e => setNewStudent({ ...newStudent, universitaly_email: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Universitaly Şifre"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.universitaly_password}
                                        onChange={e => setNewStudent({ ...newStudent, universitaly_password: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <select
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.university}
                                        onChange={e => setNewStudent({ ...newStudent, university: e.target.value })}
                                    >
                                        <option value="">Seçiniz...</option>
                                        {availableUniversities.map(u => (
                                            <option key={u.id} value={u.name}>{u.name}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Bölüm"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.program}
                                        onChange={e => setNewStudent({ ...newStudent, program: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Kullanıcı Adı"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.username}
                                        onChange={e => setNewStudent({ ...newStudent, username: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Şifre"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.password}
                                        onChange={e => setNewStudent({ ...newStudent, password: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <select
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.status}
                                        onChange={e => setNewStudent({ ...newStudent, status: e.target.value })}
                                    >
                                        {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Ücret"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.fee}
                                        onChange={e => setNewStudent({ ...newStudent, fee: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Sonuç"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.result}
                                        onChange={e => setNewStudent({ ...newStudent, result: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="Sınavlar"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.exams}
                                        onChange={e => setNewStudent({ ...newStudent, exams: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10 bg-red-600/10">
                                    <input
                                        placeholder="Deadline"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.deadline}
                                        onChange={e => setNewStudent({ ...newStudent, deadline: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10 bg-yellow-400/10">
                                    <input
                                        placeholder="Notlar"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.notes}
                                        onChange={e => setNewStudent({ ...newStudent, notes: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="CISIA K.Adı"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.cisia_username}
                                        onChange={e => setNewStudent({ ...newStudent, cisia_username: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 border border-black/10">
                                    <input
                                        placeholder="CISIA Şifre"
                                        className="w-full text-[11px] p-1 border rounded"
                                        value={newStudent.cisia_password}
                                        onChange={e => setNewStudent({ ...newStudent, cisia_password: e.target.value })}
                                    />
                                </td>
                                <td className="p-2 text-center border border-black/10">
                                    <button
                                        onClick={handleAddStudent}
                                        disabled={isAdding}
                                        className="bg-blue-600 text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
                                        title="Ekle"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </td>
                            </tr>
                            {filteredStudents.map((student, sIdx) => {
                                const apps = student.license_applications;
                                const isEven = sIdx % 2 === 0;
                                const rowBg = isEven ? 'bg-[#e0e3f1]' : 'bg-[#e9fae9]';

                                if (apps.length === 0) {
                                    return (
                                        <tr key={student.id} className={`${rowBg} hover:opacity-90`}>
                                            <td className="px-2 py-2 border border-black/10 font-bold">{student.first_name} {student.is_eu ? '(EU)' : ''}</td>
                                            <td className="px-2 py-2 border border-black/10 font-bold">{student.last_name}</td>
                                            <td className="px-2 py-2 border border-black/10">{student.universitaly_data?.email}</td>
                                            <td className="px-2 py-2 border border-black/10">{student.universitaly_data?.password}</td>
                                            <td colSpan={12} className="px-2 py-2 border border-black/10 text-center text-gray-500 italic">Başvuru kaydı yok</td>
                                            <td className="px-2 py-2 border border-black/10 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => handleAddApplication(student.id)}
                                                        className="text-green-600 hover:bg-green-100 p-1.5 rounded-lg bg-white shadow-sm"
                                                        title="Yeni başvuru ekle"
                                                        aria-label={`${student.first_name} ${student.last_name} için yeni başvuru ekle`}
                                                    >
                                                        <Plus size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteStudent(student)}
                                                        disabled={deletingStudentIds.has(student.id)}
                                                        className="text-red-600 hover:bg-red-100 p-1.5 rounded-lg bg-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                                        title="Öğrenciyi sil"
                                                        aria-label={`${student.first_name} ${student.last_name} kaydını sil`}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                return apps.map((app, index) => {
                                    return (
                                        <tr key={app.id} className={`${rowBg} hover:opacity-90`}>
                                            {index === 0 && (
                                                <>
                                                    <td rowSpan={apps.length} className="px-2 py-2 border border-black/10 font-bold align-middle bg-white/50">
                                                        <input
                                                            className="w-full bg-transparent border-transparent hover:border-gray-300 rounded px-1"
                                                            value={student.first_name}
                                                            onChange={(e) => updateStudentState(student.id, 'first_name', e.target.value)}
                                                        />
                                                    </td>
                                                    <td rowSpan={apps.length} className="px-2 py-2 border border-black/10 font-bold align-middle bg-white/50">
                                                        <input
                                                            className="w-full bg-transparent border-transparent hover:border-gray-300 rounded px-1"
                                                            value={student.last_name}
                                                            onChange={(e) => updateStudentState(student.id, 'last_name', e.target.value)}
                                                        />
                                                    </td>
                                                    <td rowSpan={apps.length} className="px-2 py-2 border border-black/10 align-middle bg-white/50">
                                                        <input
                                                            className="w-full bg-transparent border-transparent hover:border-gray-300 rounded px-1"
                                                            value={student.universitaly_data?.email || ''}
                                                            onChange={(e) => updateStudentState(student.id, 'email', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>
                                                    <td rowSpan={apps.length} className="px-2 py-2 border border-black/10 align-middle bg-white/50">
                                                        <input
                                                            className="w-full bg-transparent border-transparent hover:border-gray-300 rounded px-1"
                                                            value={student.universitaly_data?.password || ''}
                                                            onChange={(e) => updateStudentState(student.id, 'password', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>
                                                </>
                                            )}

                                            <td className="px-1 py-1 border border-black/10">
                                                <select
                                                    className="w-full bg-white border rounded px-1 h-7 text-xs focus:ring-1 focus:ring-blue-500"
                                                    value={app.university || ''}
                                                    onChange={e => updateAppState(student.id, app.id, 'university', e.target.value)}
                                                >
                                                    <option value="">Seçiniz...</option>
                                                    {availableUniversities.map(u => (
                                                        <option key={u.id} value={u.name}>{u.name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <input className="w-full bg-white border rounded px-1 focus:ring-1 focus:ring-blue-500" value={app.program || ''} onChange={e => updateAppState(student.id, app.id, 'program', e.target.value)} />
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <input className="w-full bg-white border rounded px-1 focus:ring-1 focus:ring-blue-500" value={app.username || ''} onChange={e => updateAppState(student.id, app.id, 'username', e.target.value)} />
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <input className="w-full bg-white border rounded px-1 focus:ring-1 focus:ring-blue-500" value={app.password || ''} onChange={e => updateAppState(student.id, app.id, 'password', e.target.value)} />
                                            </td>
                                            <td className={`px-1 py-1 border border-black/10 font-bold text-center`}>
                                                <select className={`w-full border rounded px-1 focus:ring-1 focus:ring-blue-500 ${app.status === 'TAMAMLANDI' ? 'bg-[#00ff00] text-black' : 'bg-white'}`} value={app.status || ''} onChange={e => updateAppState(student.id, app.id, 'status', e.target.value)}>
                                                    {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            </td>
                                            <td className={`px-1 py-1 border border-black/10 text-center`}>
                                                <input className={`w-full border rounded px-1 focus:ring-1 focus:ring-blue-500 ${app.fee?.toLowerCase().includes('ödendi') ? 'bg-[#00ff00] text-black font-bold' : 'bg-white'}`} value={app.fee || ''} onChange={e => updateAppState(student.id, app.id, 'fee', e.target.value)} />
                                            </td>
                                            <td className={`px-1 py-1 border border-black/10 font-bold`}>
                                                <input className={`w-full border rounded px-1 focus:ring-1 focus:ring-blue-500 ${app.result?.toLowerCase().includes('kabul') ? 'text-green-600' : 'bg-white'}`} value={app.result || ''} onChange={e => updateAppState(student.id, app.id, 'result', e.target.value)} />
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <input className="w-full bg-white border rounded px-1 focus:ring-1 focus:ring-blue-500" value={app.exams || ''} onChange={e => updateAppState(student.id, app.id, 'exams', e.target.value)} />
                                            </td>
                                            <td className={`px-1 py-1 border border-black/10 text-center border-black/20`}>
                                                <input className={`w-full border rounded px-1 text-black focus:ring-1 focus:ring-blue-500 ${app.deadline ? 'bg-red-500 text-white font-bold' : 'bg-white'}`} value={app.deadline || ''} onChange={e => updateAppState(student.id, app.id, 'deadline', e.target.value)} />
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <textarea className="w-full bg-white/90 border rounded px-1 min-h-[40px] focus:ring-1 focus:ring-blue-500 text-xs" value={app.notes || ''} onChange={e => updateAppState(student.id, app.id, 'notes', e.target.value)} />
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <input className="w-full bg-white border rounded px-1 focus:ring-1 focus:ring-blue-500" value={app.cisia_username || ''} onChange={e => updateAppState(student.id, app.id, 'cisia_username', e.target.value)} />
                                            </td>
                                            <td className="px-1 py-1 border border-black/10">
                                                <input className="w-full bg-white border rounded px-1 focus:ring-1 focus:ring-blue-500" value={app.cisia_password || ''} onChange={e => updateAppState(student.id, app.id, 'cisia_password', e.target.value)} />
                                            </td>

                                            <td className="px-1 py-1 border border-black/10 text-center space-x-1 whitespace-nowrap">
                                                <div className="flex flex-col gap-1 items-center justify-center">
                                                    <div className="flex gap-1">
                                                        <button onClick={() => handleSave(student)} className="text-green-600 hover:bg-green-100 p-1 rounded bg-white shadow-sm" title="Kaydet"><Save size={14} /></button>
                                                        <button onClick={() => handleDelete(student.id, app.id)} className="text-red-600 hover:bg-red-100 p-1 rounded bg-white shadow-sm" title="Sil"><Trash2 size={14} /></button>
                                                    </div>
                                                    {index === apps.length - 1 && (
                                                        <button onClick={() => handleAddApplication(student.id)} className="text-blue-600 hover:bg-blue-100 p-1 rounded bg-white shadow-sm w-full flex justify-center" title="Yeni Başvuru"><Plus size={14} /></button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                });
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LicenseStudentList;
